/**
 * List rows stop reacting to the mouse while the page or a table scrolls under
 * a pointer resting on them.
 *
 * WHY THIS EXISTS
 * A wheel scroll moves the rows under a mouse that is standing still, so every
 * row the pointer crosses turns hovered and back: its tint and left rail change,
 * and its hover pop-ups (Collections' "+N more" card, the comment and bounce
 * tooltips) open and close. Each of those is a restyle and a repaint of the
 * table, many times a second, for rows nobody is pointing at. Measured on a
 * 50-row Collections page: ~411ms of painting per 20 wheel steps with the mouse
 * over the rows against ~22ms with it over the sidebar. A pop-up opening under
 * the pointer mid-scroll also takes the wheel and stops the table.
 *
 * WHAT IT DOES
 * While a scroll that started with the pointer on a list table's rows is
 * running (until 150ms after its last scroll event), a transparent SHIELD
 * covers that table, so the pointer is over the shield and nothing under it
 * hovers. Hover comes back the moment the mouse moves after the scroll stops.
 *
 * WHY A SHIELD AND NOT `pointer-events: none` ON THE ROWS
 * That was the first version. `pointer-events` is inherited, so switching it
 * restyled every element in the table body -- 6,275 on a 50-row page, 50-75ms
 * each time, twice a scroll -- and Chrome repainted the whole table both times.
 * Showing one element costs ~0.1ms.
 *
 * WHY INSIDE THE TABLE'S SCROLLER
 * The wheel scrolls the nearest scroller above whatever the pointer is on. The
 * shield is a child of the table's own sideways scroller, so a sideways swipe
 * that starts while it is up still scrolls the table, and an up/down one passes
 * on to the page. A shield over the whole screen would send it to the page only.
 *
 * Decided once per scroll, when it starts, from the last pointer position: the
 * sidebar, filters or pager have no rows to protect, and a sideways scroll
 * moves nothing under a frozen (sticky) cell. Touch input has no hover and
 * never engages it. The scroll listener is on `window` in the capture phase, so
 * it also hears the table wrappers' sideways scroll (`scroll` does not bubble,
 * but it is captured).
 */

const SETTLE_MS = 150;
const SHIELD_CLASS = "o-scroll-shield";
// The list roots whose tables get the hover pause.
const ROWS =
  ":is(.o-list, .o-leads, .o-los, .o-lms, .o-collections, .o-acct, .o-pay, .o-rep, .o-team) .v-data-table tbody";

/* One shield per table scroller, made the first time it is needed and kept
   (hidden) after that; it goes away with the table when the list reloads. */
const shields = new WeakMap();

let timer = 0;
let active = null;
let pointerX = null;
let pointerY = null;

function onPointerMove(e) {
  if (e.pointerType === "touch") {
    pointerX = pointerY = null;
  } else {
    pointerX = e.clientX;
    pointerY = e.clientY;
  }
}

function onPointerLeave() {
  pointerX = pointerY = null;
}

function shieldFor(wrapper) {
  let shield = shields.get(wrapper);
  if (!shield) {
    shield = document.createElement("div");
    shield.className = SHIELD_CLASS;
    shield.setAttribute("aria-hidden", "true");
    // Positioned against the scroller so it scrolls with the table; Vuetify
    // leaves the wrapper static.
    if (getComputedStyle(wrapper).position === "static") wrapper.style.position = "relative";
    wrapper.appendChild(shield);
    shields.set(wrapper, shield);
  }
  return shield;
}

function engage(sideways) {
  if (pointerX === null) return null;
  const el = document.elementFromPoint(pointerX, pointerY);
  if (!el || !el.closest(ROWS)) return null;
  const cell = sideways && el.closest("td");
  if (cell && getComputedStyle(cell).position === "sticky") return null;
  const wrapper = el.closest(".v-data-table__wrapper");
  const table = wrapper && wrapper.querySelector(":scope > table");
  if (!table) return null;
  const shield = shieldFor(wrapper);
  // The whole table, not just the part in view: a sideways scroll moves the
  // rest of it under the pointer.
  shield.style.width = table.offsetWidth + "px";
  shield.style.display = "block";
  return shield;
}

function settle() {
  timer = 0;
  if (!active) return;
  active.style.display = "none";
  active = null;
}

function onScroll(e) {
  if (timer) clearTimeout(timer);
  else active = engage(e.target !== document);
  // The timer runs either way, so the decision holds for the whole scroll.
  timer = setTimeout(settle, SETTLE_MS);
}

export default {
  install() {
    if (typeof window === "undefined") return;
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onPointerLeave, {
      passive: true,
    });
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
  },
};
